export interface AiReceptionistStats {
  callsCount: number;
  callDurationMinutes: number;
  opportunitiesCount: number;
}

export interface AiReceptionistActivity {
  activity_id: string;
  enabled: boolean;
}

export interface AiReceptionistMotif {
  motif_id: string;
  mode: 'automatic' | 'custom';
  custom_instruction?: string;
}

export interface AiReceptionistConfig {
  activities: AiReceptionistActivity[];
  motifs: AiReceptionistMotif[];
  other: string;
}

// Matches IAiReceptionistTriggers (libs/utils INumber.ts): the cases where Donna takes the call
export interface AiReceptionistTriggers {
  do_not_disturb: boolean;
  unreachable: boolean;
  outside_hours: boolean;
  vacation: boolean;
  no_answer: boolean;
  declined: boolean;
  busy: boolean;
}

export interface AiReceptionistProfile {
  fullname: string;
  first_message: string;
  public_label: string;
  callback_label: string;
  ring_count: number;
  // Absent when the backend predates the setting: not sent back on save, so the stored value is kept
  triggers?: AiReceptionistTriggers;
  enabled: boolean;
  is_provisioned: boolean;
}

export interface AiReceptionistActivityCatalogItem {
  id: string;
  label: string;
  description: string;
}

export interface AiReceptionistMotifCatalogItem {
  id: string;
  label: string;
  description: string;
  recognition: string;
  default_flow: string;
  activities: string[];
}

export interface AiReceptionistMotifsCatalog {
  activities: AiReceptionistActivityCatalogItem[];
  motifs: AiReceptionistMotifCatalogItem[];
  first_message: string;
}

export interface AiReceptionistManagedByLeedflow {
  ton: string;
  tone: string;
  goal: string;
  closing: string;
  guardrails: string;
}

export interface AiReceptionistListRow {
  subscription_id: string;
  // Absent when the backend predates them
  usernames?: string[];
  sender?: string;
  fullname: string;
  activities: AiReceptionistActivity[];
  motifs: AiReceptionistMotif[];
  enabled: boolean;
  is_provisioned: boolean;
  stats: AiReceptionistStats;
}

export interface AiReceptionistDetail {
  profile: AiReceptionistProfile;
  config: AiReceptionistConfig;
  stats: AiReceptionistStats;
  catalog: AiReceptionistMotifsCatalog;
  managed_by_leedflow: AiReceptionistManagedByLeedflow;
}

// Same config the SIP webhook sends to ElevenLabs on a real call, plus a short-lived WebRTC token
export interface AiReceptionistTestSession {
  conversation_token: string;
  conversation_config_override: {
    agent: {
      prompt: { prompt: string };
      first_message: string;
      language: string;
    };
    tts?: { voice_id: string };
  };
  dynamic_variables: Record<string, string>;
}

export type AiReceptionistCallUrgency = 'immediate' | 'today' | 'this_week' | 'not_urgent' | 'unknown';

// Mirrors ICallAiSummary (libs/utils/src/types/interfaces/Call/ICall.ts)
export interface AiReceptionistCallSummary {
  motif?: string;
  motif_label?: string;
  urgency?: AiReceptionistCallUrgency;
  urgency_reason?: string;
  caller_name?: string;
  callback_number?: string;
  pending_actions?: string[];
}

export interface AiReceptionistCallContact {
  id: string;
  firstname: string;
  lastname: string;
  phone: string;
  created: number | null;
  has_tags: boolean;
}

export interface AiReceptionistCallTask {
  id: string;
  title: string;
  description: string;
  timestamp: number;
  status: 'todo' | 'done' | 'archived';
}

// Matches backend getAiReceptionistCalls (backend/src/helpers/calls.helpers.js)
export interface AiReceptionistCall {
  id: string;
  subscription_id: string;
  assistant_name: string;
  created: number;
  duration_ms: number;
  customer: string;
  contact: AiReceptionistCallContact | null;
  transcription: string;
  // The SRT itself is fetched when the transcript drawer opens (getCompanyAiReceptionistCallTranscript)
  has_transcript: boolean;
  // Same for the audio: its signed URL is fetched when the call is shown (getCompanyAiReceptionistCallRecording)
  has_recording: boolean;
  ai_summary: AiReceptionistCallSummary | null;
  readed_frontend: boolean;
  // The call comes from the current user's own receptionist, so opening it marks it read
  is_own: boolean;
  tasks: AiReceptionistCallTask[];
}

// Matches backend RING_COUNT_OPTIONS (backend/src/helpers/ai.helpers.js)
export const RING_COUNT_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10];

// Matches DEFAULT_AI_RECEPTIONIST_TRIGGERS (libs/utils): unset triggers mean every case
export const DEFAULT_AI_RECEPTIONIST_TRIGGERS: AiReceptionistTriggers = {
  do_not_disturb: true,
  unreachable: true,
  outside_hours: true,
  vacation: true,
  no_answer: true,
  declined: true,
  busy: true,
};

// Matches backend CUSTOM_INSTRUCTION_MAX_LENGTH (backend/src/helpers/ai.helpers.js)
export const CUSTOM_INSTRUCTION_MAX_LENGTH = 1000;
